import React, { useEffect, useMemo, useRef, useState } from "react";

import RHFSelect from "@/components/form/RHFSelect";
import CalendarTypeService from "@/services/calendarTypeService";

const NONE_OPTION_VALUE = "";

/**
 * Calendar selection for Create/Edit/Clone job (all three render JobForm).
 * Structural sibling of ShiftSelector, but simpler: one optional selection,
 * no predefined/custom split. The list is open to any authenticated tenant
 * user — every job creator needs the picker, most hold no tenant_config
 * permission at all.
 *
 * Uses RHFSelect (not the raw Select) so this field's height, padding and
 * label match every other simple dropdown on the job form (employeeType,
 * jobPriority, currency, assignedTo) instead of the more bespoke trigger
 * ShiftSelector rolls for its predefined/custom toggle.
 *
 * Fetched unfiltered rather than `is_active`-filtered, for the same reason
 * as ShiftSelector: a job already holding a since-disabled calendar must
 * still resolve to a label, or the field reads as unset and every
 * unrelated save 422s on the disabled calendar.
 */
const CalendarTypeSelector = ({ formMethods, disabled = false }) => {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);

  const { watch } = formMethods;
  const calendarTypeId = watch("calendarTypeId");

  // The calendar the job arrived with, captured the first time the form
  // actually holds one — not at mount. On an edit the job loads after this
  // component mounts, so a mount-time capture held "", a since-disabled
  // calendar was filtered out of the options, and the picker read empty over a
  // value the form was still carrying. Captured once either way, so switching
  // to another calendar does not keep the disabled one on the list.
  const attachedCalendarTypeId = useRef(calendarTypeId || "");
  const [attachedId, setAttachedId] = useState(calendarTypeId || "");
  useEffect(() => {
    if (!attachedCalendarTypeId.current && calendarTypeId) {
      attachedCalendarTypeId.current = calendarTypeId;
      setAttachedId(calendarTypeId);
    }
  }, [calendarTypeId]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const result = await CalendarTypeService.listAll();
        if (active) setCalendars(result);
      } catch (err) {
        console.error("Failed to load calendar types:", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(() => {
    const selectable = calendars.filter((c) => c.isActive || c.id === attachedId);
    return [
      { value: NONE_OPTION_VALUE, label: "No calendar" },
      ...selectable.map((c) => ({
        value: c.id,
        label: `${c.name}${c.isActive ? "" : " — disabled"}`,
      })),
    ];
  }, [calendars, attachedId]);

  return (
    <RHFSelect
      name="calendarTypeId"
      label="Calendar"
      options={options}
      searchable
      searchPlaceholder="Search calendars..."
      placeholder={loading ? "Loading calendars..." : "Select a calendar"}
      disabled={disabled || loading}
    />
  );
};

export default CalendarTypeSelector;
